/**
 * Graceful recovery when a lazy JS chunk fails to load after a deploy.
 *
 * Cookie-based skew protection keeps most sessions aligned, but pins can
 * expire or break (max-age, deleted deploy, SameSite gaps). In those cases
 * Vite's dynamic import fails — often because the SPA rewrite returns HTML
 * for a missing /assets/* URL. Reloading once fetches a fresh document + pin.
 */

// Imported from the posthog module directly rather than the 'helpers/logging'
// barrel: this file runs before app boot and the barrel pulls in react-query
// and router hooks.
import { captureOkr } from 'helpers/logging/posthog';

const RELOAD_SESSION_KEY = 'chunk_load_recovery_reload';

const CHUNK_LOAD_ERROR_PATTERNS = [
  /loading chunk [\w-]+ failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /failed to load module script/i,
  /expected a javascript module script/i,
  /mime type.*text\/html/i,
];

// One chunk failure is usually detected twice in the same page lifecycle
// (helpers/react lazy wrapper, then ErrorBoundary). Once a reload is in
// flight, later detectors must still get `true` so they suppress their error
// UI instead of rendering it while the reload lands.
let reloadInFlight = false;
let reportedFailure = false;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  if (typeof error === 'object') {
    const err = error as { name?: string; message?: string };
    if (err.name === 'ChunkLoadError') return true;
    if (
      typeof err.message === 'string' &&
      matchesChunkLoadMessage(err.message)
    ) {
      return true;
    }
  }

  if (typeof error === 'string' && matchesChunkLoadMessage(error)) {
    return true;
  }

  return false;
}

function matchesChunkLoadMessage(message: string): boolean {
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    const { message } = error as { message?: unknown };
    if (typeof message === 'string') return message;
  }
  return String(error);
}

export function hasAttemptedChunkLoadReload(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearChunkLoadReloadFlag(): void {
  reloadInFlight = false;
  reportedFailure = false;
  try {
    sessionStorage.removeItem(RELOAD_SESSION_KEY);
  } catch {
    // sessionStorage may be unavailable (private mode / blocked).
  }
}

function triggerReloadOnce(reason: string): boolean {
  if (reloadInFlight) {
    return true;
  }

  if (hasAttemptedChunkLoadReload()) {
    // The previous page load WAS the recovery reload and chunks are failing
    // again — let the error UI show instead of looping.
    if (!reportedFailure) {
      reportedFailure = true;
      captureOkr('chunk_load_recovery_failed', {
        reason: reason.slice(0, 300),
      });
    }
    return false;
  }

  try {
    sessionStorage.setItem(RELOAD_SESSION_KEY, '1');
  } catch {
    // Still attempt reload; worst case we may loop if storage is unavailable.
  }

  reloadInFlight = true;
  captureOkr('chunk_load_recovery_reload', { reason: reason.slice(0, 300) });
  window.location.reload();
  return true;
}

/**
 * If `error` looks like a failed lazy chunk load, reload once per tab session.
 * Returns true when a reload was triggered or is already in flight for this
 * page — callers should render nothing instead of an error UI.
 */
export function reloadOnceForChunkLoadError(error: unknown): boolean {
  if (!isChunkLoadError(error)) {
    return false;
  }

  return triggerReloadOnce(getErrorMessage(error));
}

/**
 * Install global listeners for chunk failures that bypass React error boundaries
 * (e.g. some TanStack Router lazy-route import rejections).
 *
 * The one-shot reload flag is cleared only after a short healthy window so a
 * recovery reload that still fails cannot loop forever. Surviving that window
 * on a load that was itself a recovery reload counts as a successful recovery.
 */
export function installChunkLoadRecovery(): void {
  if (typeof window === 'undefined') return;

  const wasRecoveryReload = hasAttemptedChunkLoadReload();

  window.setTimeout(() => {
    clearChunkLoadReloadFlag();
    if (wasRecoveryReload) {
      captureOkr('chunk_load_recovery_succeeded', {});
    }
  }, 10_000);

  window.addEventListener('unhandledrejection', (event) => {
    if (reloadOnceForChunkLoadError(event.reason)) {
      event.preventDefault();
    }
  });

  // Capture phase: <script> load failures target the element and do not bubble.
  // Matching event.filename alone is unsafe — Vite puts all app code under
  // /assets/, so uncaught runtime errors would also match and reload the page.
  window.addEventListener(
    'error',
    (event) => {
      if (
        reloadOnceForChunkLoadError(event.error) ||
        reloadOnceForChunkLoadError(event.message)
      ) {
        event.preventDefault();
        return;
      }

      const { target } = event;
      if (
        target instanceof HTMLScriptElement &&
        typeof target.src === 'string' &&
        target.src.includes('/assets/') &&
        triggerReloadOnce(target.src)
      ) {
        event.preventDefault();
      }
    },
    true,
  );
}
